
import { Box } from 'ui/box';
import { Stack } from 'ui/stack';

import Divider from 'components/shared/others/divider/divider';

import {
  BAR_MAX_HEIGHT,
  VALUE_LABEL_SPACE,
} from 'containers/user/goals/goals-monitoring/monitoring-chart/components/constants';
import { pluralizeMetric } from 'containers/user/goals/goals-monitoring/monitoring-chart/components/metric-block.helpers';

interface PlaceholderBlockProps {
  metricKey: string;
  previousYear?: string | number;
  currentYear?: string | number;
}

interface ColumnArgs {
  height: number;
  label?: string | number;
  bgcolor: string;
}

const PlaceholderBlock = ({
  metricKey,
  previousYear,
  currentYear,
}: PlaceholderBlockProps): React.JSX.Element => {
  const rawMetricType = metricKey.replace(/([A-Z])/g, ' $1').trim();
  const metricType = pluralizeMetric(rawMetricType);

  const renderColumn = ({ height, bgcolor }: ColumnArgs): React.JSX.Element => (
    <Stack
      sx={{
        textAlign: 'center',
        justifyContent: 'flex-end',
        px: 1.25,
        width: '100%',
      }}
    >
      <Box
        style={{ height: `${BAR_MAX_HEIGHT + VALUE_LABEL_SPACE}px` }}
        sx={{ position: 'relative', width: '100%', overflow: 'hidden' }}
      >
        <Box
          sx={{
            transitionProperty: 'all',
            transitionTimingFunction: 'cubic-bezier(0, 0, 0.2, 1)',
            transitionDuration: '700ms',
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            bgcolor,
          }}
          style={{
            height: `${height}px`,
          }}
        />
      </Box>
      <Box
        sx={{
          height: '0.75rem',
          width: '2.5rem',
          bgcolor: 'tokens.navy.100',
          borderRadius: '0.125rem',
          mx: 'auto',
          mt: 0.5,
        }}
      />
    </Stack>
  );

  const renderChangeRow = (
    width: number,
    bgcolor: string
  ): React.JSX.Element => (
    <Stack
      direction="row"
      sx={{
        alignItems: 'baseline',
        justifyContent: 'space-between',
        gap: 1,
        minWidth: 0,
      }}
    >
      <Box
        sx={{ height: '0.75rem', bgcolor }}
        style={{ width: `${width}px` }}
      />
      <Box sx={{ height: '0.75rem', width: '2rem', bgcolor }} />
    </Stack>
  );

  return (
    <Box sx={{ textAlign: 'center' }}>
      <Box
        sx={{
          textAlign: 'center',
          fontWeight: 600,
          color: 'tokens.gray.700',
          mb: 1,
          textTransform: 'capitalize',
          px: 1.25,
        }}
      >
        {metricType}
      </Box>

      <Stack
        direction="row"
        sx={{ justifyContent: 'space-between', alignItems: 'flex-end' }}
      >
        {renderColumn({
          height: 65,
          label: previousYear,
          bgcolor: 'tokens.navy.200',
        })}
        {renderColumn({
          height: 85,
          label: currentYear,
          bgcolor: 'tokens.navy.100',
        })}
      </Stack>

      <Divider customClass="my-2" />
      <Stack
        sx={{
          mt: 2,
          fontSize: '0.75rem',
          lineHeight: '1rem',
          textAlign: 'left',
          color: 'tokens.gray.500',
          fontWeight: 400,
          gap: 0.5,
          px: 1.25,
        }}
      >
        {renderChangeRow(96, 'tokens.navy.100')}
        {renderChangeRow(48, 'tokens.navy.100')}
      </Stack>
    </Box>
  );
};

export default PlaceholderBlock;
